import React, { useEffect, useState, useMemo } from "react";
import { Button, Space, Tag, Modal, Checkbox, Row, Col, Typography, Avatar, Popover, Select, Tabs } from "antd";
import {
    EditOutlined,
    ReloadOutlined,
    SafetyOutlined,
    UserOutlined,
    UserAddOutlined,
    MailOutlined,
    PhoneOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
    HistoryOutlined,
    KeyOutlined,
    CloseOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import Breadcrumbs from "../../component/Breadcrumbs";
import AdminServ from "../../service/AdminServ";
import Notify from "../../util/Notify";
import EmptyState from "../../util/EmptyState";
import { OutlineButton, PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import { getImageUrl } from "../../component/SafeImage";
import AddAdminModal from "../../component/AddAdminModal";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import DataTable from "../../component/ui/DataTable";
import { useAdminTable } from "../hooks/useAdminTable";
import dayjs from "dayjs";
const { Text } = Typography;
const { Option } = Select;

const adminPermissionsList = [
    { label: "Manage Users", value: "manage_users", group: "User Management" },
    { label: "Manage Sellers", value: "manage_sellers", group: "User Management" },
    { label: "Manage Products", value: "manage_products", group: "Product Management" },
    { label: "Manage Categories", value: "manage_categories", group: "Product Management" },
    { label: "Manage Brands", value: "manage_brands", group: "Product Management" },
    { label: "Manage Orders", value: "manage_orders", group: "Orders" },
    { label: "Manage Messages", value: "manage_messages", group: "Communication" },
    { label: "Manage Credits", value: "manage_credits", group: "Communication" },
    { label: "Manage Banners", value: "manage_banners", group: "Marketing" },
    { label: "Manage Coupons", value: "manage_coupons", group: "Marketing" },
    { label: "Manage Offers", value: "manage_offers", group: "Marketing" },
    { label: "Manage Deliveries", value: "manage_deliveries", group: "Delivery" },
    { label: "Manage Settings", value: "manage_settings", group: "Settings" },
    { label: "Full Access", value: "full_access", group: "Full Access" },
];

const MAX_VISIBLE = 2;

const PermissionsTags = ({ permissions }) => {
    if (!permissions || permissions.length === 0) {
        return <Text type="secondary" style={{ fontSize: 12 }}>No permissions</Text>;
    }

    const visible = permissions.slice(0, MAX_VISIBLE);
    const overflow = permissions.slice(MAX_VISIBLE);

    const tagStyle = {
        fontSize: 10.5,
        padding: "2px 10px",
        borderRadius: 6,
        lineHeight: "18px",
        margin: 0,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };
    const formatLabel = (p) => p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
            {visible.map((p) => (
                <Tag key={p} style={{
                    ...tagStyle,
                    background: '#f1f5ff',
                    color: '#4f46e5',
                    border: '1px solid #dbeafe',
                    fontWeight: 700
                }}>
                    {formatLabel(p)}
                </Tag>
            ))}
            {overflow.length > 0 && (
                <Popover
                    title={<span style={{ fontSize: 13, fontWeight: 600 }}>All Permissions</span>}
                    content={
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 260 }}>
                            {overflow.map((p) => (
                                <Tag key={p} color="blue" style={tagStyle}>{formatLabel(p)}</Tag>
                            ))}
                        </div>
                    }
                    trigger="hover"
                    placement="bottomLeft"
                >
                    <Tag style={{ ...tagStyle, cursor: "pointer", background: "#f0f0f0", borderColor: "#d9d9d9", color: "#555", fontWeight: 600 }}>
                        +{overflow.length}
                    </Tag>
                </Popover>
            )}
        </div>
    );
};

const AdminPermissionsPage = () => {
    const [permissionModal, setPermissionModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [addAdminModal, setAddAdminModal] = useState(false);
    const [viewAdminModal, setViewAdminModal] = useState(false);
    const [viewAdmin, setViewAdmin] = useState(null);
    const [editAdmin, setEditAdmin] = useState(null);
    const [editAdminModal, setEditAdminModal] = useState(false);

    const [quickFilterDate, setQuickFilterDate] = useState("all");

    const fetchAdmins = async (params) => {
        const query = {
            limit: params.limit,
            page: params.page
        };
        if (params.search) query.search = params.search;
        if (params.quickFilter && params.quickFilter !== "all") query.role = params.quickFilter;
        if (params.dateRange?.[0]) query.startDate = params.dateRange[0].startOf("day").toISOString();
        if (params.dateRange?.[1]) query.endDate = params.dateRange[1].endOf("day").toISOString();

        const res = await AdminServ.getAllAdmins(query);
        return { data: res?.data || [], total: res?.total || 0 };
    };

    const {
        data: admins,
        total,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchAdmins, "admin_permissions", {
        page: 1, limit: 10, search: "", quickFilter: "all", dateRange: null
    });

    const fetchAllAdmins = async () => {
        const res = await AdminServ.getAllAdmins({ limit: 10000 });
        return res?.data || [];
    };

    const handleReset = () => {
        setParams({ page: 1, limit: 10, search: "", quickFilter: "all", dateRange: null });
        setQuickFilterDate("all");
        refresh();
    };

    const handleEditPermissions = (admin) => {
        setSelectedAdmin(admin);
        setSelectedPermissions(admin.permissions || []);
        setPermissionModal(true);
    };

    const handleSavePermissions = async () => {
        setSaving(true);
        try {
            await AdminServ.updateAdminPermissions({
                adminId: selectedAdmin._id,
                permissions: selectedPermissions,
            });
            Notify.success("Permissions updated successfully");
            setPermissionModal(false);
            refresh();
        } catch (err) {
            Notify.error(err, "Failed to update permissions");
        } finally {
            setSaving(false);
        }
    };

    // ── Table Columns Matching Image 2 ──
    const columns = [
        {
            title: "IMAGE",
            key: "image",
            width: 80,
            align: "center",
            render: (_, record) => {
                const img = record.profilePhoto || record.profileImage || record.image;
                return (
                    <Avatar
                        size={36}
                        src={img ? getImageUrl(img) : null}
                        icon={<UserOutlined style={{ color: '#64748b' }} />}
                        style={{
                            borderRadius: 10,
                            border: '1px solid #e2e8f0',
                            background: '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                );
            },
        },
        {
            title: "FULL NAME",
            key: "fullName",
            width: 200,
            render: (_, record) => (
                <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e293b' }}>
                    {record.firstName} {record.lastName}
                </div>
            ),
        },
        {
            title: "EMAIL",
            dataIndex: "email",
            key: "email",
            width: 220,
            render: (email) => (
                <div style={{ fontSize: '12.5px', color: '#64748b', fontWeight: 500 }}>
                    {email}
                </div>
            )
        },
        {
            title: "ROLE",
            dataIndex: "role",
            key: "role",
            width: 120,
            align: "center",
            render: (role) => (
                <Tag
                    style={{
                        margin: 0,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontWeight: 800,
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        background: role === 'superadmin' ? '#fef2f2' : '#f5f3ff',
                        color: role === 'superadmin' ? '#ef4444' : '#8b5cf6',
                        border: `1.2px solid ${role === 'superadmin' ? '#fecaca' : '#ddd6fe'}`
                    }}
                >
                    {role}
                </Tag>
            ),
        },
        {
            title: "JOINED DATE",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 150,
            align: "center",
            render: (date) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#64748b', fontSize: '12px', fontWeight: 600 }}>
                    <CalendarOutlined style={{ color: '#94a3b8', fontSize: '13px' }} />
                    {dayjs(date).format("DD MMM YYYY")}
                </div>
            ),
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setViewAdmin(record);
                        setViewAdminModal(true);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-container">
                {/* ── Header Matching Image 2 Style ── */}
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <SafetyOutlined style={{ fontSize: '22px', color: '#4f46e5' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Admin Permissions Management</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{total} administrator accounts in system</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '8px' }}>
                            <TableActions
                                onFetchFullData={fetchAllAdmins}
                                columns={columns}
                                fileName="admin_permissions"
                                pageName="Admin Permissions Management"
                            />
                        </div>
                        <Button
                            type="primary"
                            onClick={() => setAddAdminModal(true)}
                            style={{
                                height: '40px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)',
                                border: 'none',
                                padding: '0 20px',
                                fontSize: '13px',
                                fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(255, 136, 0, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <UserAddOutlined style={{ fontSize: '16px' }} />
                            Create Admin
                        </Button>
                    </div>
                </div>

                {/* Role Tabs Matching Image 2 */}
                <div style={{ marginBottom: '24px' }}>
                    <Tabs
                        activeKey={params.quickFilter}
                        onChange={(key) => setParams(prev => ({ ...prev, quickFilter: key, page: 1 }))}
                        items={[
                            { key: "all", label: "All Admins" },
                            { key: "superadmin", label: "Super Admins" },
                            { key: "admin", label: "Admins" },
                        ]}
                        className="premium-tabs-minimal"
                    />
                </div>

                {/* ── Filters ── */}
                <div className="pcu-padding-0">
                    <div className="ss-filter-bar" style={{ background: 'transparent', border: 'none', padding: '0 0 16px 0', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>SEARCH ADMINS</span>
                            <div className="ss-search-wrap">
                                <SearchInput
                                    placeholder="Search name or email..."
                                    value={params.search}
                                    onChange={(val) => handleSearch(val)}
                                    style={{ height: '34px', borderRadius: '10px', fontSize: '13px' }}
                                />
                            </div>
                        </div>

                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>ROLE</span>
                            <Select
                                placeholder="All Roles"
                                style={{ width: '100%', height: '34px' }}
                                onChange={(val) => setParams(prev => ({ ...prev, quickFilter: val, page: 1 }))}
                                value={params.quickFilter}
                                className="pcu-radius-10"
                            >
                                <Option value="all">All Roles</Option>
                                <Option value="superadmin">Super Admin</Option>
                                <Option value="admin">Admin</Option>
                            </Select>
                        </div>

                        <div className="ss-filter-field">
                            <QuickFilter
                                label="QUICK FILTER"
                                value={null}
                                onChange={(key, range) => {
                                    setParams(prev => ({ ...prev, dateRange: range, page: 1 }));
                                }}
                            />
                        </div>

                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>JOINED RANGE</span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <DateRangePicker
                                        value={params.dateRange}
                                        onChange={(val) => setParams(prev => ({ ...prev, dateRange: val, page: 1 }))}
                                        showLabel={false}
                                        style={{ height: '34px', borderRadius: '10px' }}
                                    />
                                </div>
                                <button
                                    className="ss-reset-btn"
                                    onClick={handleReset}
                                    style={{
                                        height: '34px',
                                        width: '34px',
                                        padding: '0',
                                        borderRadius: '10px',
                                        background: '#fff',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                    title="Reset Filters"
                                >
                                    <ReloadOutlined style={{ fontSize: '14px' }} />
                                </button>
                            </div>
                        </div>

                        <div className="ss-filter-field" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div className="ss-table-wrap pcu-margin-top-16">
                        <DataTable
                            columns={columns}
                            data={admins}
                            rowKey="_id"
                            loading={loading}
                            size="small"
                            bordered={false}
                            rowClassName={(record) => {
                                const role = record.role?.toLowerCase();
                                if (role === "superadmin") return "row-highlight-rejected";
                                if (role === "admin") return "row-highlight-pending-approval";
                                return "perm-row";
                            }}
                            locale={{
                                emptyText: (
                                    <div className="pcu-padding-y-60">
                                        <EmptyState
                                            title="No Admins Found"
                                            description="No administrator accounts match your filters."
                                            gradient="orange"
                                            onAction={() => setAddAdminModal(true)}
                                            actionText="Add Admin"
                                            icon="inbox"
                                        />
                                    </div>
                                ),
                            }}
                            pagination={{
                                current: params.page,
                                pageSize: params.limit,
                                total: total,
                                showTotal: (total) => (
                                    <span className="pagination-total" style={{ background: '#fff7ed', color: '#c2410c', padding: '4px 12px', borderRadius: '100px', fontWeight: 700, fontSize: '11px' }}>Total {total} items</span>
                                ),
                                onChange: (p) => {
                                    setParams(prev => ({ ...prev, page: p }));
                                    const pageContent = document.querySelector('.page-content');
                                    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                                },
                                onShowSizeChange: (_, size) => setParams(prev => ({ ...prev, limit: size, page: 1 })),
                                position: ["bottomRight"],
                                size: "small"
                            }}
                        />
                    </div>
                </div>
            </div>

            <AddAdminModal
                open={addAdminModal}
                onClose={() => setAddAdminModal(false)}
                refresh={refresh}
            />

            <AddAdminModal
                open={editAdminModal}
                onClose={() => {
                    setEditAdminModal(false);
                    setEditAdmin(null);
                }}
                refresh={refresh}
                admin={editAdmin}
            />

            {/* ── Edit Permissions Modal ── */}
            <Modal
                title={
                    <Space>
                        <SafetyOutlined style={{ color: "var(--nearzon-orange, #ef6c00)" }} />
                        <span style={{ fontWeight: 700, fontSize: 15 }}>
                            Edit Permissions — {selectedAdmin?.firstName} {selectedAdmin?.lastName}
                        </span>
                    </Space>
                }
                open={permissionModal}
                onOk={handleSavePermissions}
                onCancel={() => setPermissionModal(false)}
                okText="Save Changes"
                confirmLoading={saving}
                width={660}
            >
                <div style={{ paddingTop: 8 }}>
                    <Text type="secondary" style={{ display: "block", marginBottom: 14, fontSize: 13 }}>
                        Select the modules this admin can access. "Full Access" grants all permissions.
                    </Text>

                    {/* Select/Unselect All */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                        <Button
                            type="dashed"
                            size="small"
                            onClick={() => {
                                if (selectedPermissions.length === adminPermissionsList.length) {
                                    setSelectedPermissions([]);
                                } else {
                                    setSelectedPermissions(adminPermissionsList.map((p) => p.value));
                                }
                            }}
                        >
                            {selectedPermissions.length === adminPermissionsList.length ? "Unselect All" : "Select All"}
                        </Button>
                    </div>

                    <Checkbox.Group
                        style={{ width: "100%" }}
                        value={selectedPermissions}
                        onChange={setSelectedPermissions}
                    >
                        <Row gutter={[10, 10]}>
                            {adminPermissionsList.map((permission) => {
                                const isSelected = selectedPermissions.includes(permission.value);
                                return (
                                    <Col span={12} key={permission.value}>
                                        <div
                                            style={{
                                                border: `1.5px solid ${isSelected ? "#ef6c00" : "#e8e8e8"}`,
                                                borderRadius: 8,
                                                padding: "8px 12px",
                                                background: isSelected ? "rgba(239,108,0,0.04)" : "#fafafa",
                                                transition: "border-color 0.15s, background 0.15s",
                                            }}
                                        >
                                            <Checkbox value={permission.value}>
                                                <Text
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: isSelected ? 600 : 400,
                                                        color: isSelected ? "#1a1a2e" : "#555",
                                                    }}
                                                >
                                                    {permission.label}
                                                </Text>
                                            </Checkbox>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Checkbox.Group>
                </div>
            </Modal>

            <style>{`
                .perm-row td {
                    vertical-align: middle !important;
                    padding-top: 10px !important;
                    padding-bottom: 10px !important;
                }
            `}</style>

            {/* ── Admin Details Modal Matching Image 2 ── */}
            <Modal
                open={viewAdminModal}
                onCancel={() => setViewAdminModal(false)}
                footer={null}
                width={650}
                centered
                closable={false}
                styles={{ body: { padding: 0 } }}
                maskStyle={{ backdropFilter: 'blur(4px)' }}
                style={{ borderRadius: 24, overflow: 'hidden' }}
            >
                {viewAdmin && (
                    <div style={{ background: '#fff', borderRadius: 24, overflow: 'hidden' }}>
                        {/* Header Section - Compact Style */}
                        <div style={{
                            padding: '16px 24px',
                            background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            position: 'relative'
                        }}>
                            <button
                                onClick={() => setViewAdminModal(false)}
                                style={{ position: 'absolute', right: 16, top: 16, background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', transition: 'all 0.2s' }}
                                onMouseEnter={e => e.currentTarget.style.color = '#1e293b'}
                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                <CloseOutlined />
                            </button>

                            <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, background: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Avatar
                                    size={50}
                                    src={(viewAdmin.profilePhoto || viewAdmin.profileImage) ? getImageUrl(viewAdmin.profilePhoto || viewAdmin.profileImage) : null}
                                    icon={<UserOutlined style={{ color: '#64748b' }} />}
                                    style={{ borderRadius: 8, background: '#f1f5f9' }}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                                        {viewAdmin.firstName} {viewAdmin.lastName}
                                    </h2>
                                    <Tag color="success" style={{ margin: 0, borderRadius: 5, padding: '0 8px', fontSize: 9, fontWeight: 800, border: 'none', background: '#fff', color: '#16a34a', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', border: '1px solid #bbf7d0' }}>
                                        ACTIVE
                                    </Tag>
                                </div>
                                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                                    ID: <span style={{ color: '#4f46e5', fontWeight: 700 }}>{viewAdmin.userId || viewAdmin._id?.substring(0, 12).toUpperCase()}</span> • {viewAdmin.role?.toUpperCase()}
                                </p>
                            </div>
                        </div>

                        <div style={{ padding: '12px 20px', background: '#fff' }}>
                            {/* Detail Grid Section - Extra Compact */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                {/* Email - Full Width */}
                                <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1.2px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <MailOutlined style={{ color: '#3b82f6', fontSize: 11 }} /> EMAIL ADDRESS
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 700, lineHeight: 1.1 }}>{viewAdmin.email}</div>
                                </div>

                                {/* Phone & Role */}
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1.2px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <PhoneOutlined style={{ color: '#3b82f6', fontSize: 11 }} /> PHONE NUMBER
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 800 }}>{viewAdmin.phone || 'N/A'}</div>
                                </div>
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1.2px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <UserOutlined style={{ color: '#3b82f6', fontSize: 11 }} /> ACCOUNT ROLE
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 800 }}>{viewAdmin.role?.toUpperCase()}</div>
                                </div>

                                {/* User ID Full width */}
                                <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1.2px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: 11 }} /> USER ID
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 700, fontFamily: 'monospace' }}>{viewAdmin.userId || viewAdmin._id}</div>
                                </div>

                                {/* Dates */}
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1.2px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <CalendarOutlined style={{ color: '#3b82f6', fontSize: 11 }} /> JOINED DATE
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 800 }}>
                                        {dayjs(viewAdmin.createdAt).format("DD MMM YYYY, HH:mm")}
                                    </div>
                                </div>
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1.2px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <ReloadOutlined style={{ color: '#3b82f6', fontSize: 11 }} /> LAST UPDATED
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 800 }}>
                                        {dayjs(viewAdmin.updatedAt).format("DD MMM YYYY, HH:mm")}
                                    </div>
                                </div>
                            </div>

                            {/* Permissions Section - Very Compact */}
                            <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '10px' }}>
                                    <SafetyOutlined style={{ fontSize: '15px', color: '#4f46e5' }} />
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>Assigned Permissions</h3>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {viewAdmin.role === 'superadmin' ? (
                                        <Tag color="gold" style={{ padding: '2px 10px', borderRadius: 6, fontWeight: 800, fontSize: 10 }}>FULL SYSTEM ACCESS</Tag>
                                    ) : (
                                        viewAdmin.permissions?.map(p => (
                                            <Tag key={p} style={{
                                                margin: 0,
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                fontWeight: 700,
                                                fontSize: '11px',
                                                background: '#f8fafc',
                                                border: '1.5px solid #e2e8f0',
                                                color: '#475569'
                                            }}>
                                                {p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                            </Tag>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer Section - Extra Compact */}
                        <div style={{ padding: '12px 20px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', background: '#fff' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                    icon={<HistoryOutlined style={{ fontSize: 12 }} />}
                                    style={{
                                        background: 'none', border: '1px solid #e2e8f0', color: '#64748b', fontSize: 12, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, height: 32, borderRadius: 8, padding: '0 12px'
                                    }}
                                >
                                    History
                                </Button>
                                <Button
                                    icon={<EditOutlined style={{ fontSize: 12 }} />}
                                    style={{
                                        background: '#eff6ff', border: 'none', color: '#3b82f6', fontSize: 11, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, height: 32, borderRadius: 8, padding: '0 10px'
                                    }}
                                    onClick={() => {
                                        setViewAdminModal(false);
                                        setEditAdmin(viewAdmin);
                                        setEditAdminModal(true);
                                    }}
                                >
                                    Edit Data
                                </Button>
                                <Button
                                    icon={<SafetyOutlined style={{ fontSize: 12 }} />}
                                    style={{
                                        background: '#f5f3ff', border: 'none', color: '#8b5cf6', fontSize: 11, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, height: 32, borderRadius: 8, padding: '0 10px'
                                    }}
                                    onClick={() => {
                                        setViewAdminModal(false);
                                        handleEditPermissions(viewAdmin);
                                    }}
                                    disabled={viewAdmin.role === 'superadmin'}
                                >
                                    Edit Perm
                                </Button>
                                <Button
                                    icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                                    style={{
                                        background: 'none', border: '1px solid #fef2f2', color: '#ef4444', fontSize: 12, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, height: 32, borderRadius: 8, padding: '0 12px'
                                    }}
                                >
                                    Remove
                                </Button>
                            </div>

                            <Button
                                onClick={() => setViewAdminModal(false)}
                                style={{
                                    height: 32, padding: '0 20px', borderRadius: 8, border: '1px solid #e2e8f0',
                                    background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 800,
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
            <style>{`
                .perm-row td {
                    vertical-align: middle !important;
                    padding-top: 6px !important;
                    padding-bottom: 6px !important;
                }
            `}</style>
        </div>
    );
};

export default AdminPermissionsPage;
